import * as api from '@apis/calendar-events';
import type { CalendarEventsPage } from '@apis/calendar-events';
import * as feedApi from '@apis/feeds-v2';
import CalendarEventFormModal from '@components/calendar-events/CalendarEventFormModal';
import { DEEPLINK_ACTION_LABELS } from '@components/feed/DeepLinkActionEdit';
import { CalendarEvent } from '@models/calendar-event';
import { DeepLinkActions } from '@models/common';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { cdnImageUrl } from '@utils/image';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Badge, Button, Calendar, Input, Modal, Pagination, Switch, Tag } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import React, { useEffect, useMemo, useState } from 'react';

type MainFeedV2Config = {
  type: 'main-feed-v2-config';
  data?: Array<{ type: string; shuffle?: boolean; [key: string]: any }>;
  [key: string]: any;
};

const formatDeeplinkSummary = (event: CalendarEvent) => {
  const actionLabel = event.action ? DEEPLINK_ACTION_LABELS[event.action]?.label || event.action : '미설정';
  if (
    (event.action === DeepLinkActions.OPEN_LINK || event.action === DeepLinkActions.OPEN_OS_BROWSER) &&
    event.payload?.url
  ) {
    return `${actionLabel}: ${event.payload.url}`;
  }
  if (event.payload?.title) {
    return `${actionLabel}: ${event.payload.title}`;
  }
  if (event.payload?.keyword) {
    return `${actionLabel}: ${event.payload.keyword}`;
  }
  return actionLabel;
};

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const QUERY_KEY = 'calendar-events';
const PAGE_SIZE = 20;

const formatEventPeriod = (start: string | Date, end: string | Date) => {
  const s = dayjs(start);
  const e = dayjs(end);
  if (s.isSame(e, 'day')) {
    return s.format('M월 D일');
  }
  return `${s.format('M월 D일')} ~ ${e.format('M월 D일')}`;
};

export const CalendarEventsEdit: React.FC = () => {
  const dialog = useOkCancelDialog();
  const queryClient = useQueryClient();
  const [isManageOpen, setIsManageOpen] = useState(false);
  const [panelMonth, setPanelMonth] = useState<Dayjs>(dayjs());
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | undefined>();
  const [shuffle, setShuffle] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);

  const configQuery = useQuery({
    queryKey: ['main-feed-v2-config'],
    queryFn: () => feedApi.getMainFeedV2Config(),
  });

  useEffect(() => {
    const config = (configQuery.data?.data || {}) as MainFeedV2Config;
    const calendarEventConfig = (config.data || []).find((d) => d?.type === 'calendar-event-config');
    setShuffle(calendarEventConfig?.shuffle ?? false);
  }, [configQuery.data]);

  const saveShuffleMutation = useMutation({
    mutationFn: async (checked: boolean) => {
      const existing = (configQuery.data?.data || {}) as MainFeedV2Config;
      const existingItems = Array.isArray(existing.data) ? existing.data : [];
      const nextItems = [
        ...existingItems.filter((d) => d?.type !== 'calendar-event-config'),
        { type: 'calendar-event-config', shuffle: checked },
      ];
      const payload: MainFeedV2Config = {
        ...(existing || {}),
        type: 'main-feed-v2-config',
        data: nextItems,
      };
      return await feedApi.updateMainFeedV2Config(payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['main-feed-v2-config'] });
      dialog.open({ type: 'ok', title: '알림', content: '무작위 노출 설정을 저장했습니다.' });
    },
    onError: (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e?.message || JSON.stringify(e) });
    },
  });

  const calendarFrom = panelMonth.startOf('month').startOf('week').toISOString();
  const calendarTo = panelMonth.endOf('month').endOf('week').toISOString();

  const listFrom = selectedDate
    ? selectedDate.startOf('day').toISOString()
    : panelMonth.startOf('month').startOf('week').toISOString();
  const listTo = selectedDate
    ? selectedDate.endOf('day').toISOString()
    : panelMonth.endOf('month').endOf('week').toISOString();
  const skip = (page - 1) * PAGE_SIZE;

  const calendarEventsQuery = useQuery({
    queryKey: [QUERY_KEY, 'calendar', calendarFrom, calendarTo],
    queryFn: () => api.getCalendarEvents({ from: calendarFrom, to: calendarTo }),
    enabled: isManageOpen,
  });

  const listEventsQuery = useQuery({
    queryKey: [QUERY_KEY, 'list', listFrom, listTo, searchKeyword, skip, PAGE_SIZE],
    queryFn: () =>
      api.getCalendarEvents({
        from: listFrom,
        to: listTo,
        search: searchKeyword || undefined,
        skip,
        limit: PAGE_SIZE,
      }),
    enabled: isManageOpen,
  });

  const calendarEvents = useMemo(() => {
    const body = calendarEventsQuery.data?.data;
    return Array.isArray(body) ? body : [];
  }, [calendarEventsQuery.data]);

  const listPage = useMemo((): CalendarEventsPage => {
    const body = listEventsQuery.data?.data;
    if (body && !Array.isArray(body) && Array.isArray(body.contents)) {
      return body;
    }
    return { contents: [], total: 0, skip, limit: PAGE_SIZE, hasNext: false };
  }, [listEventsQuery.data, skip]);

  const filteredEvents = listPage.contents;

  const deleteMutation = useMutation({
    mutationFn: api.deleteCalendarEvent,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      dialog.open({ type: 'ok', title: '알림', content: '행사를 삭제했습니다.' });
    },
    onError: (e: any) => {
      dialog.open({ type: 'ok', title: '오류', content: e?.message || JSON.stringify(e) });
    },
  });

  const countByDay = useMemo(() => {
    const map = new Map<string, number>();
    calendarEvents.forEach((event) => {
      let cursor = dayjs(event.startDate).startOf('day');
      const end = dayjs(event.endDate).startOf('day');
      while (cursor.isSameOrBefore(end, 'day')) {
        const key = cursor.format('YYYY-MM-DD');
        map.set(key, (map.get(key) || 0) + 1);
        cursor = cursor.add(1, 'day');
      }
    });
    return map;
  }, [calendarEvents]);

  const resetListPage = () => setPage(1);

  const handleSearch = (value: string) => {
    setSearchKeyword(value.trim());
    resetListPage();
  };

  const handleDelete = (event: CalendarEvent) => {
    dialog.open({
      type: 'ok_cancel',
      title: '삭제 확인',
      content: `"${event.title}" 행사를 삭제할까요?`,
      onConfirm: () => deleteMutation.mutate(event._id),
    });
  };

  const openCreate = () => {
    setFormMode('create');
    setEditingEvent(undefined);
    setFormOpen(true);
  };

  const openEdit = (event: CalendarEvent) => {
    setFormMode('edit');
    setEditingEvent(event);
    setFormOpen(true);
  };

  const dateCellRender = (date: Dayjs) => {
    const count = countByDay.get(date.format('YYYY-MM-DD')) || 0;
    if (!count) return null;
    return (
      <div style={{ marginTop: 0, lineHeight: 1 }}>
        <Badge count={count} style={{ backgroundColor: '#bd334a' }} overflowCount={99} size="small" />
      </div>
    );
  };

  return (
    <>
      <div>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 8 }}>
          <h3 style={{ fontSize: 18, fontWeight: 600 }}>캘린더 행사 설정(발견)</h3>
          <Button
            onClick={() => {
              setSelectedDate(null);
              setPanelMonth(dayjs());
              setSearchInput('');
              setSearchKeyword('');
              setPage(1);
              setIsManageOpen(true);
            }}>
            수정
          </Button>
        </div>
      </div>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 32 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <span>캘린더 행사 관리</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 400 }}>무작위 노출</span>
                <Switch
                  checked={shuffle}
                  loading={saveShuffleMutation.isPending}
                  onChange={(checked) => {
                    setShuffle(checked);
                    saveShuffleMutation.mutate(checked);
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                size="small"
                onClick={() => {
                  setSelectedDate(null);
                  resetListPage();
                }}>
                전체보기
              </Button>
              <Button size="small" type="primary" onClick={openCreate}>
                행사 추가
              </Button>
            </div>
          </div>
        }
        width={1200}
        open={isManageOpen}
        onCancel={() => setIsManageOpen(false)}
        centered
        styles={{
          body: {
            maxHeight: 'calc(100vh - 180px)',
            overflow: 'hidden',
            paddingTop: 12,
            paddingBottom: 12,
          },
        }}
        footer={<Button onClick={() => setIsManageOpen(false)}>닫기</Button>}>
        <style>{`
          .calendar-events-manage .ant-picker-calendar-full .ant-picker-panel .ant-picker-calendar-date {
            margin: 0 2px;
            padding: 2px 4px;
            height: 56px;
          }
          .calendar-events-manage .ant-picker-calendar-full .ant-picker-content th {
            padding: 4px 0;
            height: auto;
          }
          .calendar-events-manage .ant-picker-calendar-header {
            padding: 0;
          }
        `}</style>
        <div
          className="calendar-events-manage"
          style={{ display: 'flex', gap: 16, height: 'calc(100vh - 220px)', maxHeight: 620 }}>
          <div
            style={{
              flex: '0 0 58%',
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 8,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Calendar
              fullscreen
              value={selectedDate || panelMonth}
              onPanelChange={(value) => {
                setPanelMonth(value);
                resetListPage();
              }}
              onSelect={(value, info) => {
                if (info.source === 'date') {
                  setSelectedDate(value);
                  setPanelMonth(value);
                  resetListPage();
                  return;
                }
                setPanelMonth(value);
                setSelectedDate(null);
                resetListPage();
              }}
              dateCellRender={dateCellRender}
              headerRender={({ value, onChange }) => {
                const current = value || panelMonth;
                const goToMonth = (offset: number) => {
                  onChange(current.add(offset, 'month'));
                };
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12,
                      padding: '4px 0 8px',
                    }}>
                    <Button
                      type="text"
                      size="small"
                      icon={<LeftOutlined />}
                      onClick={() => goToMonth(-1)}
                      aria-label="이전 달"
                    />
                    <div style={{ minWidth: 120, textAlign: 'center', fontWeight: 600, fontSize: 15 }}>
                      {current.format('YYYY년 M월')}
                    </div>
                    <Button
                      type="text"
                      size="small"
                      icon={<RightOutlined />}
                      onClick={() => goToMonth(1)}
                      aria-label="다음 달"
                    />
                  </div>
                );
              }}
            />
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
            <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 15, flexShrink: 0 }}>
              {selectedDate ? `${selectedDate.format('YYYY년 M월 D일')} 행사` : '전체 행사 목록'}
              <span style={{ marginLeft: 8, color: '#888', fontWeight: 400 }}>({listPage.total})</span>
            </div>
            <Input.Search
              allowClear
              placeholder="제목 또는 장소 검색"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={handleSearch}
              style={{ marginBottom: 12, flexShrink: 0 }}
            />
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}>
              {listEventsQuery.isLoading && <div style={{ color: '#999' }}>불러오는 중...</div>}
              {!listEventsQuery.isLoading && filteredEvents.length === 0 && (
                <div style={{ color: '#999', padding: '32px 0', textAlign: 'center' }}>
                  {searchKeyword ? '검색 결과가 없습니다.' : '등록된 행사가 없습니다.'}
                </div>
              )}
              {filteredEvents.map((event) => (
                <div
                  key={event._id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: 12,
                    border: '1px solid #eee',
                    borderRadius: 10,
                    background: '#fafafa',
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 4,
                      flexShrink: 0,
                    }}>
                    {event.priority && (
                      <Tag color="gold" style={{ margin: 0, fontSize: 12, lineHeight: '20px', padding: '0 4px' }}>
                        우선순위노출
                      </Tag>
                    )}
                    <img
                      src={cdnImageUrl(event.imageUrl)}
                      alt=""
                      style={{
                        width: 72,
                        height: 72,
                        objectFit: 'cover',
                        borderRadius: 8,
                        background: '#eee',
                      }}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, color: '#888' }}>{event.location}</div>
                    <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{event.title}</div>
                    <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
                      {formatEventPeriod(event.startDate, event.endDate)}
                    </div>
                    <div style={{ fontSize: 12, color: '#1677ff', marginTop: 4, wordBreak: 'break-all' }}>
                      {formatDeeplinkSummary(event)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Button size="small" onClick={() => openEdit(event)}>
                      수정
                    </Button>
                    <Button size="small" danger loading={deleteMutation.isPending} onClick={() => handleDelete(event)}>
                      삭제
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            {listPage.total > 0 && (
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <Pagination
                  size="small"
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={listPage.total}
                  showSizeChanger={false}
                  onChange={(nextPage) => setPage(nextPage)}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>

      <CalendarEventFormModal
        open={formOpen}
        mode={formMode}
        initialData={editingEvent}
        onClose={() => setFormOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        }}
      />
    </>
  );
};

export default CalendarEventsEdit;
